import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bomb, AlertTriangle, RefreshCw, Trash2, Shield } from 'lucide-react';
import { formatLastActive } from './_utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BulkDeleteCard — admin housekeeping tool with two safeguard tiers
 * (server-mirror in `routes/admin.py`):
 *   1. Test-account cleanup — single-admin, server-locked to a
 *      hard-coded pattern (smoke* / phase2 / beta@test / g3-free-* /
 *      tier-gate-test-*). Confirmation phrase required.
 *   2. Two-admin co-approval banner — surfaces pending requests
 *      authored by OTHER admins that need MY approval. The "request a
 *      bulk delete" UI itself lives next to the user table (TBD); this
 *      card focuses on test cleanup + showing the approval queue.
 */
const BulkDeleteCard = ({ onAfterDelete }) => {
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  // Two-admin queue
  const [pending, setPending] = useState({ awaiting_my_approval: [], mine_pending: [] });
  const [pendingBusy, setPendingBusy] = useState(null); // request id being acted on

  const loadPreview = async () => {
    setPreviewLoading(true);
    setPreviewError('');
    try {
      const { data } = await axios.get(`${API}/admin/users/bulk-preview?pattern=test`);
      setPreview(data);
    } catch (err) {
      setPreviewError(err?.response?.data?.detail || err?.message || 'Preview failed.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadPending = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users/bulk-delete-requests`);
      setPending(data);
    } catch {
      /* non-fatal */
    }
  };

  useEffect(() => {
    loadPreview();
    loadPending();
  }, []);

  const runTestDelete = async () => {
    if (!preview || preview.count === 0) return;
    if (confirmation !== preview.confirmation_required) return;
    setDeleting(true);
    setPreviewError('');
    try {
      const { data } = await axios.post(`${API}/admin/users/bulk-delete-tests`, {
        confirmation_phrase: confirmation,
      });
      setDeleteResult(data);
      setConfirmation('');
      await loadPreview();
      if (onAfterDelete) await onAfterDelete();
    } catch (err) {
      setPreviewError(err?.response?.data?.detail || err?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const approveRequest = async (id) => {
    if (!window.confirm('Approve this bulk delete? It runs immediately on the requester\'s target list.')) return;
    setPendingBusy(id);
    try {
      await axios.post(`${API}/admin/users/bulk-delete-approve/${id}`);
      await loadPending();
      if (onAfterDelete) await onAfterDelete();
    } catch (err) {
      window.alert(err?.response?.data?.detail || err?.message || 'Approve failed.');
    } finally {
      setPendingBusy(null);
    }
  };

  const cancelRequest = async (id) => {
    if (!window.confirm('Cancel this pending bulk-delete request?')) return;
    setPendingBusy(id);
    try {
      await axios.post(`${API}/admin/users/bulk-delete-cancel/${id}`);
      await loadPending();
    } catch (err) {
      window.alert(err?.response?.data?.detail || err?.message || 'Cancel failed.');
    } finally {
      setPendingBusy(null);
    }
  };

  const phraseOk = preview && confirmation === preview.confirmation_required;

  return (
    <div className="bg-enf-surface-deep border border-enf-role-warning/30 rounded-xl p-6 mb-10" data-testid="admin-bulk-delete-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Bomb className="w-4 h-4 text-enf-role-warning" />
          <div className="text-sm font-semibold uppercase tracking-widest text-enf-text-secondary">Bulk delete tools</div>
        </div>
        <button
          onClick={() => { loadPreview(); loadPending(); }}
          disabled={previewLoading}
          className="text-xs text-enf-role-info hover:text-enf-text-primary flex items-center gap-1.5 transition-colors"
          data-testid="bulk-delete-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${previewLoading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* ----- Tier 2 approval banner (only if something is pending) ----- */}
      {pending.awaiting_my_approval.length > 0 && (
        <div className="mb-5 p-4 bg-enf-accent/10 border border-enf-accent-fg/40 rounded-md" data-testid="bulk-pending-approval-banner">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-enf-accent-fg" />
            <div className="text-sm font-semibold text-enf-text-primary">
              {pending.awaiting_my_approval.length} bulk-delete request{pending.awaiting_my_approval.length === 1 ? '' : 's'} awaiting your approval
            </div>
          </div>
          <div className="space-y-2">
            {pending.awaiting_my_approval.map((req) => (
              <div key={req.id} className="p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md" data-testid={`pending-request-${req.id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="text-xs text-enf-text-secondary">
                    <span className="font-semibold text-enf-text-primary">{req.created_by_email}</span> wants to delete{' '}
                    <span className="font-semibold text-enf-role-warning">{req.target_emails.length} user{req.target_emails.length === 1 ? '' : 's'}</span>
                  </div>
                  <div className="text-[10px] text-enf-text-muted">{formatLastActive(req.created_at)}</div>
                </div>
                {/* Lifetime/paid warning — surfaces when the request includes
                    Founder/Hero/Enferno-tier accounts so the approver sees
                    the financial implication before pulling the trigger. */}
                {req.lifetime_count > 0 && (
                  <div className="mb-2 p-2 bg-enf-role-warning/15 border border-enf-role-warning/40 rounded text-[11px] text-enf-role-warning leading-snug" data-testid={`pending-lifetime-warning-${req.id}`}>
                    <span className="font-bold">⚠ Includes {req.lifetime_count} lifetime/paid account{req.lifetime_count === 1 ? '' : 's'}</span> (Founder / Hero / Enferno). Approving will permanently delete their training history. Stripe subscriptions need a separate manual refund if applicable.
                    {req.lifetime_emails && req.lifetime_emails.length > 0 && (
                      <details className="mt-1">
                        <summary className="cursor-pointer hover:text-enf-text-primary">Show paid emails</summary>
                        <ul className="mt-1 font-mono text-[10px] space-y-0.5">
                          {req.lifetime_emails.map((e) => <li key={e}>{e}</li>)}
                        </ul>
                      </details>
                    )}
                  </div>
                )}
                <div className="text-xs text-enf-text-secondary italic mb-2 border-l-2 border-enf-surface-border pl-2">"{req.reason}"</div>
                <details className="mb-3">
                  <summary className="text-[11px] text-enf-text-muted cursor-pointer hover:text-enf-text-secondary">Show {req.target_emails.length} target email{req.target_emails.length === 1 ? '' : 's'}</summary>
                  <ul className="mt-2 text-[11px] font-mono text-enf-text-muted space-y-0.5 max-h-32 overflow-y-auto">
                    {req.target_emails.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                </details>
                <div className="flex gap-2">
                  <button
                    onClick={() => approveRequest(req.id)}
                    disabled={pendingBusy === req.id}
                    className="px-3 py-1.5 bg-enf-role-warning hover:bg-enf-role-warning-hover disabled:opacity-50 rounded text-xs font-semibold"
                    data-testid={`approve-request-${req.id}`}
                  >
                    Approve & execute
                  </button>
                  <button
                    onClick={() => cancelRequest(req.id)}
                    disabled={pendingBusy === req.id}
                    className="px-3 py-1.5 bg-enf-surface-mid hover:bg-enf-surface-elevated border border-enf-surface-border rounded text-xs"
                    data-testid={`cancel-request-${req.id}`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {pending.mine_pending.length > 0 && (
        <div className="mb-5 p-3 bg-enf-surface-mid border border-enf-surface-border rounded-md text-xs text-enf-text-muted" data-testid="bulk-mine-pending-banner">
          You have {pending.mine_pending.length} pending request{pending.mine_pending.length === 1 ? '' : 's'} awaiting another admin's approval.
        </div>
      )}

      {/* ----- Tier 1: Test-account cleanup ----- */}
      <div className="p-4 bg-enf-surface-mid border border-enf-surface-border rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold">Test account cleanup</div>
          <span className="text-[10px] text-enf-text-muted">Single-admin · server-locked pattern</span>
        </div>
        <p className="text-xs text-enf-text-muted leading-relaxed mb-4">
          Removes auto-generated test accounts (<code className="text-enf-text-secondary">smoke*</code>, <code className="text-enf-text-secondary">phase2</code>, <code className="text-enf-text-secondary">beta@test</code>, <code className="text-enf-text-secondary">g3-free-*</code>, <code className="text-enf-text-secondary">tier-gate-test-*</code>). The canonical test user <code className="text-enf-text-secondary">phase1@test.com</code>, admins, and lifetime-tier accounts are protected by the server and will never be deleted by this tool.
        </p>

        {previewError && (
          <div className="mb-3 px-3 py-2 bg-enf-accent/10 border border-enf-accent-fg/40 text-enf-accent-fg text-xs rounded-md flex items-center gap-2">
            <AlertTriangle className="w-3 h-3" /> {previewError}
          </div>
        )}

        {deleteResult && (
          <div className="mb-3 px-3 py-2 enf-success-banner bg-emerald-900/20 border border-emerald-700/40 text-emerald-300 text-xs rounded-md" data-testid="bulk-delete-success">
            Deleted {deleteResult.deleted_count} accounts · {deleteResult.cascaded.workouts} workouts · {deleteResult.cascaded.training_sessions} training sessions cascaded.
          </div>
        )}

        {preview ? (
          preview.count === 0 ? (
            <div className="text-sm text-enf-text-muted py-3 text-center">No test accounts match the cleanup pattern. Database is clean.</div>
          ) : (
            <>
              <div className="mb-3 text-xs text-enf-text-secondary" data-testid="bulk-preview-count">
                <span className="font-bold text-enf-role-warning">{preview.count}</span> account{preview.count === 1 ? '' : 's'} will be permanently deleted, along with all of their workouts and training sessions.
              </div>
              <details className="mb-3">
                <summary className="text-[11px] text-enf-text-muted cursor-pointer hover:text-enf-text-secondary">Show the list</summary>
                <ul className="mt-2 text-[11px] font-mono text-enf-text-muted space-y-0.5 max-h-40 overflow-y-auto">
                  {preview.matches.map((m) => <li key={m.id} data-testid={`preview-match-${m.email}`}>{m.email}</li>)}
                </ul>
              </details>
              <div className="space-y-2">
                <label className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold block">
                  Type <span className="font-mono text-enf-role-warning">{preview.confirmation_required}</span> to confirm
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={confirmation}
                    onChange={(e) => setConfirmation(e.target.value)}
                    className="flex-1 px-3 py-2 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary text-sm font-mono focus:outline-none focus:border-enf-role-warning"
                    placeholder={preview.confirmation_required}
                    data-testid="bulk-delete-confirmation-input"
                  />
                  <button
                    onClick={runTestDelete}
                    disabled={!phraseOk || deleting}
                    className="px-4 py-2 bg-enf-role-warning hover:bg-enf-role-warning-hover disabled:bg-enf-surface-elevated disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md text-sm font-semibold flex items-center gap-2 transition-colors"
                    data-testid="bulk-delete-execute"
                  >
                    <Trash2 className="w-4 h-4" /> {deleting ? 'Deleting...' : 'Delete all'}
                  </button>
                </div>
              </div>
            </>
          )
        ) : (
          <div className="text-sm text-enf-text-muted py-3 text-center">{previewLoading ? 'Loading preview…' : 'Click Refresh to load.'}</div>
        )}
      </div>

      <div className="mt-4 text-[11px] text-enf-text-muted leading-relaxed">
        <strong className="text-enf-text-secondary">Need to delete arbitrary (non-test) users in bulk?</strong> Use the two-admin
        path: hit <code className="text-enf-text-secondary">POST /api/admin/users/bulk-delete-request</code> with the user IDs +
        a reason. A second admin then approves via the banner above. This UI is backend-only for now — the multi-select on the
        Recent Signups table ships when there's a routine need (likely post-launch when IronFire takes over).
      </div>
    </div>
  );
};

export default BulkDeleteCard;
